<template>
    <!-- Header Overlay -->
    <header class="glass-morphism border-b border-white/10 sticky top-0 z-40">
      <div class="container mx-auto px-6 py-4">
        <div class="flex items-center justify-center w-full">
          <div class="flex items-center space-x-4">
            <div class="relative">
              <a href="https://www.fvv-net.de/" target="_blank" rel="noopener noreferrer">
                <img src="https://www.fvv-net.de/fileadmin/FVV/FVV-Logo-White.png" alt="FVV Logo" class="h-12 filter drop-shadow-lg">
              </a>
            </div>
          </div>
        </div>  
      </div>
    </header>

    <!-- Main Content -->
    <main class="container mx-auto px-4 md:px-6 py-4 md:py-8">
      <div class="max-w-md mx-auto space-y-6">
        <h1 class="text-2xl font-bold text-white">Event-Check-In QR-Code</h1>
      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-sky-800"></div>
        <p class="mt-4 text-gray-600">Loading QR code...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <i class="fas fa-exclamation-triangle text-red-500 text-3xl mb-3"></i>
        <h2 class="text-lg font-semibold text-red-800 mb-2">QR Code Not Found</h2>
        <p class="text-red-600">{{ error }}</p>
      </div>

      <!-- QR Code Display -->
      <div v-else-if="qrData" class="bg-white rounded-2xl shadow-xl p-8">
        <!-- QR Code Image -->
        <div class="text-center mb-6">
          <div v-if="imageLoadError" class="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-8">
            <i class="fas fa-qrcode text-gray-400 text-6xl mb-4"></i>
            <p class="text-gray-600 mb-4">QR code image cannot be loaded directly</p>
            <div class="flex flex-col sm:flex-row gap-3 justify-center">
              <button 
                @click="retryImageLoad"
                class="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                <i class="fas fa-redo mr-2"></i>
                Try Again
              </button>
              <button 
                @click="downloadQRImage"
                class="bg-sky-800 hover:bg-sky-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                <i class="fas fa-download mr-2"></i>
                Download QR Code
              </button>
            </div>
          </div>
          <img 
            v-else
            :src="proxiedImageUrl || qrData.qr_path" 
            :alt="`QR-Code für ${qrData.first_name} ${qrData.last_name}`"
            class="w-64 h-64 mx-auto border-4 border-gray-200 rounded-lg shadow-lg"
            @error="handleImageError"
          />
        </div>

        <!-- Guest Information -->
        <div class="space-y-3 mb-8">
          <div class="flex items-center justify-center space-x-2">
            <i class="fas fa-user text-sky-800"></i>
            <span class="text-lg font-semibold text-gray-800">
              {{ qrData.first_name }} {{ qrData.last_name }}
            </span>
          </div>
          
          <div class="flex items-center justify-center space-x-2">
            <i class="fas fa-building text-sky-800"></i>
            <span class="text-gray-600">{{ qrData.company }}</span>
          </div>
          
          <div class="flex items-center justify-center space-x-2">
            <i class="fas fa-calendar text-sky-800"></i>
            <span class="text-sm text-gray-600 text-center">{{ qrData.eventname }}</span>
          </div>

          <div class="flex items-center justify-center space-x-2">
            <i class="fas fa-ticket-alt text-sky-800"></i>
            <span class="text-xs font-mono text-gray-500">{{ qrData.unique_id }}</span>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="space-y-3">
          <button 
            @click="downloadPDF"
            :disabled="downloading"
            class="w-full bg-sky-800 hover:bg-sky-700 disabled:bg-sky-800 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <i class="fas fa-file-pdf" :class="{ 'animate-pulse': downloading }"></i>
            <span>{{ downloading ? 'Creating...' : 'Download as PDF' }}</span>
          </button>
        </div>
      </div>
      </div>
    </main>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'

export default {
  name: 'QRView',
  setup() {
    const route = useRoute()
    const loading = ref(true)
    const error = ref('')
    const qrData = ref(null)
    const downloading = ref(false)
    const imageLoadError = ref(false)
    const proxiedImageUrl = ref('')

    const fetchQRData = async () => {
      try {
        loading.value = true
        error.value = ''
        
        const response = await fetch('https://www.fvv-net.de/api/qrcheckin', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ uniqueId: route.params.uniqueId })
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        qrData.value = data
      } catch (err) {
        console.error('Error fetching QR data:', err)
        error.value = 'QR code could not be loaded. Please check the link.'
      } finally {
        loading.value = false
      }
    }

    const handleImageError = (event) => {
      console.error('QR code image failed to load:', event)
      
      // If we haven't tried proxy yet, try it
      if (!proxiedImageUrl.value && qrData.value) {
        // Try using a CORS proxy (you can replace this with your own proxy if needed)
        proxiedImageUrl.value = `https://cors-anywhere.herokuapp.com/${qrData.value.qr_path}`
      } else {
        // If proxy also failed, show the error state
        imageLoadError.value = true
      }
    }

    const retryImageLoad = () => {
      imageLoadError.value = false
      proxiedImageUrl.value = ''
    }

    const downloadQRImage = () => {
      if (!qrData.value) return
      
      // Create a direct download link
      const link = document.createElement('a')
      link.href = qrData.value.qr_path
      link.download = `QR-Code_${qrData.value.unique_id}.png`
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    const downloadPDF = async () => {
      if (!qrData.value) return
      
      try {
        downloading.value = true
        
        // Convert image to data URL to avoid CORS issues
        const imageDataUrl = await convertImageToDataUrl(qrData.value.qr_path)
        
        // Create PDF with enhanced layout
        const { jsPDF } = await import('jspdf')
        const pdf = new jsPDF()
        
        // Add header background
        pdf.setFillColor(0, 58, 92)
        pdf.rect(0, 0, 210, 60, 'F')
        
        // Add white title text
        pdf.setTextColor(255, 255, 255)        
        pdf.setFontSize(20)
        const eventName = qrData.value.eventname || 'Event'
        pdf.text(eventName, 105, 40, { align: 'center' })
        
        // Reset text color for content
        pdf.setTextColor(0, 0, 0)
        
        // Add guest info section with better layout
        pdf.setFontSize(12)
        pdf.text('Guest Information', 20, 75)
        
        pdf.text(`Name: ${qrData.value.first_name} ${qrData.value.last_name}`, 20, 90)
        pdf.text(`Company: ${qrData.value.company}`, 20, 105)
        pdf.text(`ID: ${qrData.value.unique_id}`, 20, 120)
        
        // Add decorative line
        pdf.setDrawColor(0, 58, 92)
        pdf.setLineWidth(0.5)
        pdf.line(20, 135, 190, 135)
        
        // Add QR code image as data URL
        if (imageDataUrl) {
          pdf.addImage(imageDataUrl, 'PNG', 55, 145, 100, 100)
        } else {
          pdf.text('QR code image could not be loaded.', 105, 175, { align: 'center' })
        }
        
        // Add footer with brand color
        pdf.setFillColor(0, 58, 92)
        pdf.rect(0, 280, 210, 20, 'F')
        
        pdf.setTextColor(255, 255, 255)
        pdf.setFontSize(10)
        pdf.text('This QR code is required for your Event Check-In', 105, 290, { align: 'center' })
        
        // Add timestamp
        pdf.setTextColor(100, 100, 100)
        pdf.setFontSize(8)
        const timestamp = new Date().toLocaleString()        
        // Download the PDF
        pdf.save(`QR-Code_${qrData.value.unique_id}.pdf`)
        
      } catch (err) {
        console.error('Error generating PDF:', err)
        alert('PDF could not be created. Please try again later.')
      } finally {
        setTimeout(() => {
          downloading.value = false
        }, 1000)
      }
    }

    const convertImageToDataUrl = (imageUrl) => {
      return new Promise((resolve) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')
            canvas.width = img.width
            canvas.height = img.height
            ctx.drawImage(img, 0, 0)
            const dataUrl = canvas.toDataURL('image/png')
            resolve(dataUrl)
          } catch (error) {
            console.error('Error converting image to data URL:', error)
            resolve(null)
          }
        }
        
        img.onerror = () => {
          console.error('Failed to load image for conversion')
          resolve(null)
        }
        
        img.src = imageUrl
      })
    }

    onMounted(() => {
      fetchQRData()
    })

    return {
      loading,
      error,
      qrData,
      downloading,
      imageLoadError,
      proxiedImageUrl,
      handleImageError,
      retryImageLoad,
      downloadQRImage,
      downloadPDF
    }
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Glass morphism effect */
.glass-morphism {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Pulse animation */
.pulse-animation {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Gradient classes */
.from-f08400 {
  --tw-gradient-from: #f08400;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgb(248 132 0 / 0));
}

.to-ff6b35 {
  --tw-gradient-to: #ff6b35;
}
</style>
